import { inject } from '@angular/core';
import {
  type HttpInterceptorFn,
  type HttpRequest,
  HttpContextToken,
  HttpErrorResponse,
} from '@angular/common/http';
import { OAuthService } from 'angular-oauth2-oidc';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SsoAuthService } from '../services/sso-auth.service';

export const SKIP_TOKEN = new HttpContextToken<boolean>(() => false);

export const tokenInterceptor: HttpInterceptorFn = (request, next) => {
  const oauthService = inject(OAuthService);
  const ssoAuthService = inject(SsoAuthService);

  if (shouldSkipToken(request, oauthService)) {
    return next(request);
  }

  const token = ssoAuthService.getAccessToken();
  const authReq = addToken(request, token);

  return next(authReq).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse)) {
        return throwError(() => error);
      }

      if (error.status !== 401 || isTokenRequest(request, oauthService)) {
        return throwError(() => error);
      }

      return from(refreshAccessToken(oauthService)).pipe(
        switchMap(refreshed => {
          if (!refreshed) {
            ssoAuthService.logOut();
            return throwError(() => error);
          }

          const refreshedToken = ssoAuthService.getAccessToken();
          return next(addToken(request, refreshedToken));
        }),
      );
    }),
  );
};

function addToken(request: HttpRequest<unknown>, accessToken: string): HttpRequest<unknown> {
  if (!accessToken) {
    return request;
  }

  return request.clone({
    headers: request.headers.set('Authorization', `Bearer ${accessToken}`),
  });
}

let refreshInFlight: Promise<boolean> | null = null;

function refreshAccessToken(oauthService: OAuthService): Promise<boolean> {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = oauthService
    .refreshToken()
    .then(() => true)
    .catch((error: unknown) => {
      console.error(error);
      return false;
    })
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
}

function isTokenRequest(request: HttpRequest<unknown>, oauthService: OAuthService): boolean {
  const tokenEndpoint = oauthService.tokenEndpoint;
  if (!tokenEndpoint) {
    return false;
  }

  return request.url.startsWith(tokenEndpoint);
}

function shouldSkipToken(request: HttpRequest<unknown>, oauthService: OAuthService): boolean {
  return (
    request.context.get(SKIP_TOKEN) ||
    isTokenRequest(request, oauthService) ||
    !isProtectedResource(request)
  );
}

function isProtectedResource(request: HttpRequest<unknown>): boolean {
  if (!isAbsoluteUrl(request.url)) {
    return true;
  }

  const hostname = getHostname(request.url);
  return hostname !== null && isTrustedHostname(hostname);
}

function isTrustedHostname(hostname: string): boolean {
  const browserHostname = getBrowserHostname();
  const issuerHostname = getHostname(environment.sso.issuer);

  return (
    hostname === browserHostname ||
    hostname === issuerHostname ||
    isEmpHostname(hostname) ||
    hostname === 'news.energysalesdirect.com'
  );
}

function isEmpHostname(hostname: string): boolean {
  return hostname === 'energymarketprice.com' || hostname.endsWith('.energymarketprice.com');
}

function getBrowserHostname(): string | null {
  return typeof window !== 'undefined' ? window.location.hostname : null;
}

function getHostname(url: string): string | null {
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
}

function isAbsoluteUrl(url: string): boolean {
  return /^[a-z][a-z\d+\-.]*:\/\//i.test(url);
}
